// constants.ts - Application constants and configuration
import { getCurrentPlatformCapabilities } from './platform/platformCapabilities';

export const APP_INFO = {
  NAME: 'Croop',
  AUTHOR: 'Cristina Melnic',
  EMAIL: 'cristina.v.melnic@gmail.com',
  INSTAGRAM: 'https://www.instagram.com/cristina.v.melnic/',
  GITHUB: 'https://github.com/cristina-v-melnic'
} as const;

export const DEMO_CREDITS = {
  CHOREOGRAPHER: 'Sandra Villinger',
  PERFORMER: 'Cristina Melnic'
} as const;

export const SEGMENT_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#FFD166',
  '#6B5CA5',
  '#F9C80E'
] as const;

export const SOURCE_SEGMENT_COLOR = '#6148AB';
export const COMBO_SEGMENT_COLOR = '#4CB5A8';

export const VIDEO_CONFIG = {
  MAX_HEIGHT_NORMAL: '24rem',
  MAX_HEIGHT_FULLSCREEN: '70vh',
  DEFAULT_PLAYBACK_RATE: 1.0,
  DEFAULT_MIRROR: true,
  DEFAULT_MUTED: true
} as const;

export const SEGMENT_CONFIG = {
  DEFAULT_TRANSITION_DURATION: 0.5,
  DEFAULT_OVERLAP: 0.3,
  DEFAULT_TRANSITION_TYPE: 'none'
} as const;

export const SESSION_CONFIG = {
  EXPIRATION_DAYS: 30,
  MAX_FILE_SIZE_MB: 500
} as const;

export const UI_DIMENSIONS = {
  LOGO_SIZE: '4rem',
  LOADING_SPINNER_SIZE: '40px',
  EMPTY_STATE_HEIGHT: '16rem',
  ICON_SIZE: 20,
  ICON_SIZE_SMALL: 16,
  ICON_SIZE_TINY: 12
} as const;

export const BUTTON_COLORS = {
  PRIMARY: SEGMENT_COLORS[3],
  SUCCESS: SEGMENT_COLORS[1],
  DANGER: SEGMENT_COLORS[0],
  WARNING: SEGMENT_COLORS[2],
  GRAY: '#6b7280',
  PURPLE: SEGMENT_COLORS[3],
  DEMO: SEGMENT_COLORS[4],
  DISABLED: '#d1d5db'
} as const;

export const TIMELINE_COLORS = {
  DEFAULT: '#dc2626',
  MERGED: '#7c3aed'
} as const;

export const LOADING_SOURCES = {
  LOCAL: 'local',
  FIREBASE: 'firebase',
  DEMO: 'demo'
} as const;

export const SPEED_OPTIONS = [
  0.25, 0.5, 0.75, 0.8, 0.9, 1, 1.1, 1.2, 1.25
] as const;

// Quotas / tiers
// Free-tier library cap shown in the UI. Keep in sync with
// FREE_UPLOAD_PROJECT_LIMIT (the enforced value) in
// components/services/uploadQuotaPolicy.ts.
export const FREE_TIER_MAX_VIDEOS = 30 as const;
export const LEGACY_FREE_TIER_MAX_VIDEOS = 5 as const;

// Firestore collection keys
export const USERS_COLL = 'users';
export const USER_VIDEOS_SUBCOLL = 'videos';
export const USER_PROJECTS_SUBCOLL = 'projects';
export const USER_PLAYLISTS_SUBCOLL = 'playlists';

// Storage roots
export const STORAGE_USER_ROOT = 'users';
export const STORAGE_USER_VIDEOS = 'videos';

// Keep MAX_SIZE_MB in sync with FREE_UPLOAD_MAX_SIZE_MB in
// components/services/uploadQuotaPolicy.ts and with the cap in storage.rules —
// this is the client-side upload gate, that one is the save-time policy, and the
// rule is what Storage actually enforces. All three must agree, or an upload
// passes both client checks and then fails as storage/unauthorized.
export const VIDEO_FILE_LIMITS = {
  WARNING_SIZE_MB: 250,     // Confirm before long uploads
  MAX_SIZE_MB: 500,         // Hard upload limit
  WARNING_SIZE_BYTES: 250 * 1024 * 1024,
  MAX_SIZE_BYTES: 500 * 1024 * 1024
} as const;

// Quarantine for MOBILE-READY-002: sharing and Stripe/payment remain retained
// implementation code, but they are not product surfaces. The legacy
// REACT_APP_ENABLE_* toggles are honored only for explicit local repair testing,
// never in production builds or hybrid mobile targets.
const platformCapabilities = getCurrentPlatformCapabilities();

// Legacy compatibility adapter for retained repair-path tests and older callers.
// New UI and route gates should ask the platform capabilities module directly.
export const FEATURE_FLAGS = {
  ENABLE_PAYMENTS: platformCapabilities.canShowPaymentUpgradeUi,
  ENABLE_SHARING: platformCapabilities.canExposeSharing
} as const;
